import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import { formatAccountNumber } from "../../../../../components/_utils/FormatAccountNumber";
import {
  ReimburseApproveAccount,
  ReimburseRejectAccount,
  viewAccountDetailR,
} from "../../../../../redux/features/accounts/ReimburseAccount";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import ICONS from "../../../../../assets/Icons";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import {
  panLookUp,
  resetPanLookup,
  resetPANVerification,
} from "../../../../../redux/features/sharedResources/sharedResources";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import { toast } from "react-toastify";
import CommonToast from "../../../../../components/_utils/CommonToast";
import accountNameStandardised from "../../../../../utils/accountNamestandardised";

const ReimbursementPanEditSucess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const updatedFields = location?.state?.updatedFields || [];
  const [previousAssociated, setPreviousAssociated] = useState(0);

  const [isFocused, setIsFocused] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const services = useSelector((state) => state.refreshToken.services);
  useEffect(() => {
    dispatch(resetPANVerification());
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchPANviewAccountDetailData = async () => {
      let response = await dispatch(viewAccountDetailR({ id }));
      if (response?.error?.message === "Request failed with status code 404") {
        navigate("*");
      }
    };
    fetchPANviewAccountDetailData();
  }, [dispatch, id, navigate]);

  useEffect(() => {
    const storedValue = localStorage.getItem("haveAssociated");
    if (storedValue !== null) {
      setPreviousAssociated(JSON.parse(storedValue));
    }
  }, []);

  const { panLookupData } = useSelector((state) => state.sharedResources);

  const { viewAccountDetailData, viewAccountDetailLoading } = useSelector(
    (state) => state.ReimburseAccount,
  );

  const beneficiaryAccount = useMemo(
    () => viewAccountDetailData?.reimbursement_accounts[0] || {},
    [viewAccountDetailData],
  );

  const viewDetailsHandler = () => {
    navigate(
      `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${id}`,
      {
        replace: true,
      },
    );
  };
  const dashboardHandler = () => {
    navigate("/reimbursement_account_dashboard", {
      replace: true,
    });
  };

  let haveApproved = panLookupData?.beneficiary_accounts?.find((val) => {
    return val?.approval_status === "approved";
  });
  let havePending = panLookupData?.beneficiary_accounts?.find((val) => {
    return val?.approval_status === "pending" && val?.id !== id;
  });

  useEffect(() => {
    if (!viewAccountDetailLoading && viewAccountDetailData) {
      dispatch(
        panLookUp({
          pan_number: viewAccountDetailData?.reimbursement_accounts[0]?.pan_no,
        }),
      );
    }
    return () => {
      dispatch(resetPanLookup());
    };
  }, [dispatch, viewAccountDetailLoading, viewAccountDetailData]);

  const approveAcc = async () => {
    UniversalRefreshToast("Approving account");
    try {
      toast.dismiss();

      const response = await dispatch(ReimburseApproveAccount(id)).unwrap();

      navigate(
        `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${id}`,
        {
          replace: true,
        },
      );
      if (response?.status) {
        CommonToast({
          text: "Account approved successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      } else {
        CommonToast({
          text:
            response?.payload?.errors?.reimbursement_account?.[0] ??
            "Something went wrong.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    } catch (err) {
      toast.dismiss();
    }
  };
  const rejectAcc = async ({ id, AccountIdData: accountIdData }) => {
    UniversalRefreshToast("Rejecting account");
    try {
      toast.dismiss();

      const response = await dispatch(
        ReimburseRejectAccount({ id, AccountIdData: accountIdData }),
      );
      navigate(
        `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${id}`,
        {
          replace: true,
        },
      );
      if (response.payload.status === true) {
        CommonToast({
          text: "Account rejected successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      } else {
        CommonToast({
          text:
            response?.payload?.errors?.reimbursement_account?.[0] ??
            "Something went wrong.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    } catch (err) {
      toast.dismiss();
    }
  };

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    const sanitizedValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
    setDeclineReason(sanitizedValue);
  };

  const buttonRows = [
    ...(services?.includes("RAAccount") || services?.includes("Admin")
      ? [
          [
            {
              btnText: "Reject account",
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                setRejectPopup(true);
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: "Approve account",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                approveAcc();
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),
    [
      // {
      //   btnText: "Create transaction",
      //   enabledBgColor: "var(--text-primary)",
      //   enabledTextColor: "var(--positive-bg)",
      //   icon: ICONS?.ADD_BUTTON_WHITE,
      //   handleFunction: createTxn,
      //   iconToBeAlign: "left",
      //   fullWidthRequired: true,
      //   imgColor: "var(--positive-bg)",
      // },
      {
        btnText: "View details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: viewDetailsHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: dashboardHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "black",
      },
    ],
  ];

  const shouldShowAssociatedTab =
    previousAssociated === false &&
    (haveApproved !== undefined || havePending !== undefined);

  const approvedAccounts =
    panLookupData?.beneficiary_accounts?.filter(
      (val) => val?.approval_status === "approved",
    ) || [];

  const pendingAccounts =
    panLookupData?.beneficiary_accounts?.filter(
      (val) => val?.approval_status === "pending" && val?.id !== id,
    ) || [];

  const fieldsToShow =
    updatedFields.length > GlobalConfiguration.lengthCheckZero
      ? updatedFields
      : ["pan_no", "gstin_detail"];

  const panGstFieldConfig = {
    pan_no: {
      leftSubTitle: "PAN number",
      getTitle: (acc) => acc?.pan_no,
    },
    gstin_detail: {
      leftSubTitle: "GST number (optional)",
      getTitle: (acc) => acc?.gstin_detail,
    },
  };

  const renderPanGstCards = () => {
    return fieldsToShow
      .filter((key) => panGstFieldConfig[key])
      .filter(
        (key) => key !== "gstin_detail" || beneficiaryAccount?.gstin_detail,
      )
      .map((key) => {
        const config = panGstFieldConfig[key];
        const title = config.getTitle(beneficiaryAccount);
        if (key === "gstin_detail" && !title) return null;
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: title || "Not Given",
                leftSubTitle: config.leftSubTitle,
              },
            ]}
          />
        );
      });
  };

  const AccountIdData = declineReason;
  return (
    <>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex">
          <div className="absolute top-top-base left-top-base h-full w-full opacity-dark-opacity bg-PopUp-BackDrop" />
          <SmallPopupWithInput
            headerTitle="Reject account"
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            secondHeaderTitle="Provide the reason for rejection."
            secondHeaderFontWeight="normal"
            secondHeaderFontColor="text-secondary"
            secondHeaderTextSize="font-xs"
            noteColor="text-secondary"
            bulletPoints={[
              "New requests and transactions cannot be created for this account.",
              "Before rejecting this account, reject all associated transactions and requests.",
              "Users can edit the rejected account to update details.",
            ]}
            subtextwithbullet="Y"
            inputPlaceholder={"Reason"}
            inputVariant={computeVariant(declineReason, isFocused)}
            inputOnFocus={(e) => setIsFocused(true)}
            inputOnBlur={(e) => setIsFocused(false)}
            btnOneIsDisabled={showLoader ? true : false}
            inputBgColor="var(--primary-bg)"
            noteText="Minimum 10 characters required."
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              if (!showLoader) {
                handleInputChange(e);
              }
            }}
            inputValue={declineReason}
            inputFontKind="big"
            inputIsFullWidth={true}
            btnOneText="Cancel"
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnTwoText="Reject"
            btnTwoHandleFunction={() => {
              setShowLoader(true);
              rejectAcc({ id, AccountIdData });
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              declineReason.trim().length <
              GlobalConfiguration.rejectReasonMinLength
            }
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      <StatusContainer
        isLoading={viewAccountDetailLoading}
        status="success"
        hoverOption="black"
        title={"Account details updated successfully."}
        buttonRows={buttonRows}
      >
        <TabsWeapperSection title="Updated details">
          <>{renderPanGstCards()}</>
        </TabsWeapperSection>
        {shouldShowAssociatedTab && (
          <TabsWeapperSection title="Accounts associated to PAN">
            <>
              {haveApproved &&
                approvedAccounts.length >
                  GlobalConfiguration.lengthCheckZero && (
                  <>
                    {approvedAccounts.map((val, index) => (
                      <InfoCard
                        key={val?.id || index}
                        bgColor="var(--positive-card)"
                        showRowDivider={true}
                        rowBorderColor="var(--positive-bg)"
                        rows={[
                          {
                            leftTitle:
                              accountNameStandardised(val?.name) ||
                              "Not available",
                            leftSubTitle: "Approved account",
                          },
                          {
                            leftTitle: formatAccountNumber(val?.bank_acc_no),
                            leftSubTitle: "Account number",
                            ifsc: val?.ifsc_code,
                          },
                        ]}
                      />
                    ))}
                  </>
                )}
              {havePending &&
                pendingAccounts.length >
                  GlobalConfiguration.lengthCheckZero && (
                  <>
                    {pendingAccounts.map((val, index) => (
                      <InfoCard
                        key={val?.id || index}
                        bgColor="var(--positive-card)"
                        showRowDivider={true}
                        rowBorderColor="var(--positive-bg)"
                        rows={[
                          {
                            leftTitle:
                              accountNameStandardised(val?.name) ||
                              "Not available",
                            leftSubTitle: "Pending account",
                          },
                          {
                            leftTitle: formatAccountNumber(val?.bank_acc_no),
                            leftSubTitle: "Account number",
                            ifsc: val?.ifsc_code,
                          },
                        ]}
                      />
                    ))}
                  </>
                )}
              {!haveApproved &&
                !havePending &&
                (!panLookupData?.beneficiary_accounts ||
                  panLookupData.beneficiary_accounts.length ===
                    GlobalConfiguration.lengthCheckZero) && (
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle:
                          "  No associated accounts found for the PAN details provided",
                        leftSubTitle:
                          " There are no existing accounts that are associated with the PAN details provided.",
                      },
                    ]}
                  />
                )}
            </>
          </TabsWeapperSection>
        )}
      </StatusContainer>
    </>
  );
};

export default ReimbursementPanEditSucess;
